import React from "react";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { format, isToday, isYesterday, parseISO } from "date-fns";
import { es } from "date-fns/locale";
import { LuStar } from "react-icons/lu";
import { emptyStateIllustrations } from "../assets/empty-states";
import { Card, CardHeader, EmptyState, Loading, MiniTrendSparkline, PageWrapper } from "../components/blocks";
import { Apis } from "../util/Apis";
import { getTrendIndicator } from "../util/general";

const DAILY_POINTS = [
  { date: "2026-05-27", points: 48 },
  { date: "2026-05-26", points: 72 },
  { date: "2026-05-25", points: 36 },
  { date: "2026-05-24", points: 0 },
  { date: "2026-05-23", points: 54 },
  { date: "2026-05-22", points: 66 },
  { date: "2026-05-21", points: 24 },
];

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatDayLabel = (dateValue) => {
  const parsedDate = parseISO(dateValue);

  if (isToday(parsedDate)) {
    return "Hoy";
  }

  if (isYesterday(parsedDate)) {
    return "Ayer";
  }

  const label = format(parsedDate, "d 'de' MMMM", { locale: es });
  return label.charAt(0).toUpperCase() + label.slice(1);
};

const Puntos = () => {
  const salaInfo = JSON.parse(localStorage.getItem("salaInfo"));

  const { data: dataCoupon, isLoading } = useQuery({
    queryKey: ["point-coupons", salaInfo?.salaId],
    queryFn: async () => {
      const res = await axios.get(Apis.CAPP + "/point-coupons?salaId=" + salaInfo.salaId);
      return res.data;
    },
    enabled: !!salaInfo?.salaId,
  });

  const pointsList = DAILY_POINTS;

  const pointsWithTrend = pointsList.map((item, index) => ({
    ...item,
    label: formatDayLabel(item.date),
    trend: getTrendIndicator(
      toNumber(item.points),
      index === pointsList.length - 1 ? NaN : toNumber(pointsList[index + 1]?.points)
    ),
  }));

  return (
    <PageWrapper>
      {isLoading ? (
        <Loading />
      ) : (
        <div className="flex flex-col w-full gap-4">
          <Card variant="dark" className="relative gap-5 overflow-hidden">
            <CardHeader
              title="Puntos"
              subtitle="Total de puntos"
              icon={<LuStar size={24} />}
              variant="dark"
            />
            <MiniTrendSparkline
              data={pointsList.map((item) => item.points)}
              className="absolute bottom-6 right-6"
            />

            <div className="flex items-baseline gap-1 px-2 pr-24">
              <span className="text-3xl font-extrabold">{Math.floor(toNumber(dataCoupon?.points))}</span>
              <span className="text-base text-slate-100">puntos</span>
            </div>
          </Card>

          {pointsWithTrend.length > 0 ? (
            <Card>
              <CardHeader
                title="Resumen diario"
                subtitle="Puntos ganados por dia en la ultima semana"
                icon={<LuStar size={24} />}
              />

              <div className="flex flex-col w-full gap-3">
                {pointsWithTrend.map((item) => (
                  <div
                    key={item.date}
                    className="flex items-center gap-3 px-4 py-3 border bg-slate-50 border-slate-100 rounded-2xl"
                  >
                    <div className="flex flex-col min-w-0">
                      <span className="text-sm font-semibold text-slate-950">{item.label}</span>
                    </div>

                    <div className="flex flex-col items-end ml-auto shrink-0">
                      <div className="flex items-center gap-1">
                        <span className="text-lg font-extrabold text-slate-950">
                          {item.points > 0 ? `+${Math.floor(item.points)}` : 0}
                        </span>
                        <item.trend.icon size={15} className={item.trend.className} />
                      </div>
                      <span className="text-xs text-slate-500">puntos</span>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          ) : (
            <EmptyState
              title="No hay informacion disponible"
              subtitle="Aun no hay puntos registrados para mostrar en esta vista"
              illustration={emptyStateIllustrations.points}
              illustrationAlt="Sin puntos registrados"
            />
          )}
        </div>
      )}
    </PageWrapper>
  );
};

export default Puntos;
